import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useField, useFormikContext } from 'formik';
import { Form as SForm, Select, Label } from 'semantic-ui-react';
import _ from 'lodash';
import { useFormikSchemaContext } from './FormikWithSchema';
import { data } from 'jquery';
import { innerText, replaceInStringElement } from '../Utils';

interface Props {
    id?: string;
    name: string;
    label?: string;
    options: { text?: string, content?: React.ReactNode; value: string | number; id?: string | number }[];
    placeholder?: string;
    limit?: number;
    clearable?: boolean;
    myAllowAdditions?: boolean;
    selection?: boolean;
    required?: boolean;
    disabled?: boolean;
    value?: string | number;
    deps?: any[];
    // [key: string]: any;
    onChange?: (event: React.SyntheticEvent<HTMLElement, Event>, data: any) => void;
}

const SemanticFormikSelect: React.FC<Props> = ({ name, label, options, limit, placeholder, onChange, deps, clearable, ...props }) => {
    const [field, meta, helper] = useField(name);
    const schema = useFormikSchemaContext();
    const isRequired = schema?.isFieldRequired(name);
    const label_ = label ?? schema?.schema?.fields?.[name]?.spec?.label;

    const [addedOptions, setAddedOptions] = useState<{ text: string; value: string | number; id?: string | number }[]>([]);


    const showError = meta.touched && !!meta.error;
    const [searchQuery, setSearchQuery] = useState('');

    const allOptions = useMemo(() => options.concat(addedOptions ?? []) || [], [options, addedOptions]);
    const filteredOptions = useMemo(() => {
        
        return allOptions.filter((opt, idx) => {
            return limit == null || idx < limit || opt.id == field.value
                || (searchQuery && (((_.deburr(opt.text as string ?? '')).toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1) || (_.deburr(innerText(opt.content))).toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1));
        }
        );
    }, [allOptions, searchQuery, limit]);

    // Sync external value (for example the `selectedId` returned by useSelectableList.getDropdownProps())
    // into Formik. We intentionally do NOT mark the field as touched here so auto-selection
    // (like selecting the first state after country changes) doesn't trigger validation UI.
    const externalValue = props.value;
    // console.log('SemanticFormikSelect: externalValue=', externalValue, ' field.value=', field.value, ' for ', name);
    const externalDeps: any[] = deps ?? [];

    useEffect(() => {
        // Only write into Formik if externalValue is defined and different from Formik's value.
        // This will let hooks that set a value (selectFirst, or selectedId update) propagate into Formik.
        if (externalValue !== undefined && externalValue !== field.value) {
            // write the external value into Formik
            helper.setValue(externalValue);
            // do not mark touched (it's an automatic sync), leave touched as-is.
            // helper.setTouched(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalValue, ...externalDeps]);

    const handleChange = useCallback(async (event: React.SyntheticEvent<HTMLElement, Event>, data: any) => {
        // console.log('SemanticFormikSelect: handleChange: ', name, ' value=', data);
        // user manually changed the select: set formik value and mark touched
        await helper.setValue(data.value);
        await helper.setTouched(true);
        if (onChange) {
            onChange(event, data);
        }
    }, [helper.setValue, helper.setTouched, onChange]);

    const handleOnBlur = useCallback(() => {
        field.onBlur(name);
    }, [name, field.onBlur]);

    const handleSearchChange = useCallback((event: React.SyntheticEvent<HTMLElement, Event>, data: any) => {
        setSearchQuery(data.searchQuery);
    }, []);

    return (
        <SForm.Field error={showError} required={props.required ?? isRequired} disabled={props.disabled}>
            {label_ && <label htmlFor={name}>{label_}</label>}
            <Select
                id={props.id ?? name}
                name={name}
                options={filteredOptions}
                value={field.value}
                placeholder={placeholder}
                onAddItem={(e, { value }) => {
                    // console.log('SemanticFormikSelect: onAddItem: ', value);
                    if (props.myAllowAdditions) {
                        const newOption = { key: `${name}-${value}`, text: value.toString(), value: value.toString(), id: value.toString() };
                        setAddedOptions((prev) => [...prev, newOption]);
                    }
                    const newOption = { text: value.toString(), value: value.toString(), key: value.toString() };
                    setAddedOptions((prev) => [...prev, newOption]);
                }}
                search
                selectOnNavigation={false}
                onSearchChange={handleSearchChange}
                onChange={handleChange}
                onBlur={handleOnBlur}
                deburr={true}
                clearable={clearable}
                selection={props.selection}
                disabled={props.disabled}
                allowAdditions={!!props.myAllowAdditions}
            />
            {showError && <Label basic color="red" pointing>{meta.error}</Label>}
        </SForm.Field>
    );
};

export default SemanticFormikSelect;